import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPriceTypeLabel, priceTypeEnum, stepModeEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateDailySubContractorMutation, useDailySubContractorMutation } from '../../../../hooks/mutations/useDailySubContractorMutation';
import { useSubContractorsQuery } from '../../../../hooks/queries/useSubContractorsQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DataItem } from '../../../../services/apiSLTP/dailyReports/activities';
import { DailySubContractor } from '../../../../services/apiSLTP/dailyReports/dailySubContractors';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageDailySubContractorModalProps extends ModalProps {
    activity: DataItem;
    activityIndex: number;
    refetch: () => void;
    dailySubContractor: DailySubContractor | null;
}

const ManageDailySubContractorModal: React.FC<ManageDailySubContractorModalProps> = ({
    open,
    handleClose,
    activity,
    refetch,
    dailySubContractor,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [selectedSubContractorType, setSelectedSubContractorType] = useState({ label: '', value: '' });
    const [currentSubContractorId, setCurrentSubContractorId] = useState(dailySubContractor?._id);
    const [price, setPrice] = useState(typeof dailySubContractor?.price === 'number' ? dailySubContractor?.price.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        dailySubContractor?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeLabel(dailySubContractor?.priceType))),
                  value: (dailySubContractor?.priceType).toString(),
              }
            : null
    );

    const { data: dataSubContractorTypes } = useSubContractorsQuery({ queries: { active: true } });

    const { mutate: updateDailySubContractor } = useDailySubContractorMutation({ onSuccess: refetch });
    const { mutate: createDailySubContractor } = useCreateDailySubContractorMutation({ onSuccess: refetch });

    const postDailySubContractor = () => {
        createDailySubContractor({
            body: {
                activityId: activity._id,
                subContractorTypeId: selectedSubContractorType.value,
                price: price ? parseFloat(price) : undefined,
                priceType: priceType?.value ? parseInt(priceType.value) : undefined,
            },
        });
    };

    const patchDailySubContractor = () => {
        if (!currentSubContractorId) return;
        updateDailySubContractor({
            id: currentSubContractorId,
            body: {
                subContractorTypeId: selectedSubContractorType.value,
                price: price ? parseFloat(price) : null,
                priceType: priceType?.value ? parseInt(priceType.value) : null,
            },
        });
    };

    useEffect(() => {
        if (dailySubContractor) {
            setSelectedSubContractorType({
                label: dailySubContractor.subContractorTypeId?.designation || '',
                value: dailySubContractor.subContractorTypeId?._id || '',
            });
        }
    }, []);

    return (
        <ModalBody
            title={t('subContractor')}
            subtitle={!!currentSubContractorId?.length ? t('update') : t('add')}
            description={activity.siteId.name}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !currentSubContractorId?.length ? postDailySubContractor() : patchDailySubContractor();
            }}
            disableSubmit={!selectedSubContractorType.value.length}
        >
            <DropDown
                selectedItem={selectedSubContractorType}
                items={dataSubContractorTypes?.map((subContractor) => ({ label: subContractor.designation, value: subContractor._id })) || []}
                handleChange={(event, value) => setSelectedSubContractorType({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('subContractor')}
                required
            />
            {activity.stepMode === stepModeEnum.VALIDATE && (
                <>
                    <CustomTextInput
                        value={price}
                        onChange={setPrice}
                        label={t('price')}
                        errorText={t('error')}
                        startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                        type="number"
                    />
                    <DropDown
                        items={Object.values(priceTypeEnum).map((type) => ({
                            label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                            value: type.toString(),
                        }))}
                        selectedItem={priceType}
                        handleChange={(e, value) => {
                            setPriceType(value);
                        }}
                        inputLabel={t('pricing')}
                        disableClearable={false}
                    />
                </>
            )}
        </ModalBody>
    );
};

export default ManageDailySubContractorModal;
